import { useSearchParams } from "react-router-dom";
import { Button, ErrorState, Spinner } from "@common/index";
import { ComponentColor } from "@common/types";
import { ROUTES } from "@routing/routes";
import { OrderCard } from "./_components/OrderCard";
import { OrdersPagination } from "./_components/OrdersPagination";
import { useMeOrders } from "./_helpers/useMeOrders";

// GetMeOrders returns the user's full order list (the query has no paging
// params server-side), so pagination here is a client-side slice. Switch to
// server-side paging if/when the endpoint grows PaginationList support.
const PAGE_SIZE = 10;

export const OrderHistory = () => {
    const { sortedOrders, isLoading, hasError, retry } = useMeOrders();

    const [searchParams, setSearchParams] = useSearchParams();
    const pageNumber = Math.max(1, Number(searchParams.get("page")) || 1);

    const totalPages = Math.ceil(sortedOrders.length / PAGE_SIZE);
    const pageOrders = sortedOrders.slice(
        (pageNumber - 1) * PAGE_SIZE,
        pageNumber * PAGE_SIZE,
    );

    const setPage = (newPage: number) => {
        const next = new URLSearchParams(searchParams);
        if (newPage <= 1) {
            next.delete("page");
        } else {
            next.set("page", String(newPage));
        }
        setSearchParams(next, { replace: false });
        if (typeof window !== "undefined") {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    return (
        <div className="bg-base-100 text-neutral min-h-[60vh]">
            <div className="max-w-3xl mx-auto px-4 py-6 md:py-8 flex flex-col gap-6">
                <h1 className="text-3xl font-bold text-neutral">
                    Order History
                </h1>

                {isLoading && (
                    <div className="flex justify-center items-center py-12">
                        <Spinner size="lg" />
                    </div>
                )}

                {!isLoading && hasError && <ErrorState onRetry={retry} />}

                {!isLoading && !hasError && sortedOrders.length === 0 && (
                    <div className="flex flex-col items-center gap-4 py-12 text-center">
                        <p className="text-neutral/70">
                            No orders yet. When you place your first order, it
                            will show up here.
                        </p>
                        <Button
                            to={ROUTES.BROWSE}
                            color={ComponentColor.Warning}
                            className="text-neutral font-bold normal-case"
                        >
                            Shop Now
                        </Button>
                    </div>
                )}

                {!isLoading && !hasError && sortedOrders.length > 0 && (
                    <>
                        <div className="flex flex-col gap-4">
                            {pageOrders.map((order) => (
                                <OrderCard key={order.id} order={order} />
                            ))}
                        </div>
                        {totalPages > 1 && (
                            <OrdersPagination
                                pageNumber={pageNumber}
                                totalPages={totalPages}
                                onPageChange={setPage}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
};
